import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import PaywallModal from "@/components/PaywallModal";

/**
 * Reusable TRCA CTA gate.
 *   - Unauthenticated → opens AuthModal
 *   - Authenticated, credits > 0 → navigates to /trca
 *   - Authenticated, credits === 0 → opens PaywallModal (Stripe Checkout)
 */
export default function TrcaCtaGate({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const evaluate = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/trca/me/status");
      if (data.can_take) {
        nav("/trca");
      } else {
        setPaywallOpen(true);
      }
    } catch (ex) {
      console.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setPaywallOpen(true);
    } finally {
      setChecking(false);
    }
  }, [nav]);

  const start = useCallback(async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    await evaluate();
  }, [user, evaluate]);

  const onAuthSuccess = useCallback(async () => {
    setAuthOpen(false);
    await evaluate();
  }, [evaluate]);

  return (
    <>
      {children({ start, loading: checking })}
      {authOpen && (
        <AuthModal
          onClose={() => setAuthOpen(false)}
          onSuccess={onAuthSuccess}
          message="Sign in or create a free account to take the TRCA Assessment."
        />
      )}
      {paywallOpen && (
        <PaywallModal
          productSlug="trca_assessment"
          onClose={() => setPaywallOpen(false)}
        />
      )}
    </>
  );
}
